import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEpcQtdFlag, isContractEpcQtd } from './useEpcQtdFlag';

export interface CurvaSPoint {
  data: string;
  pv: number;
  ev: number;
  ac: number;
  /** Forecast: projected AC at completion based on CPI */
  eac_proj?: number;
}

export interface CurvaSData {
  points: CurvaSPoint[];
  bac: number;
  eac: number | null;
  cpi: number | null;
}

/**
 * Hook that fetches Curva S data (PV/EV/AC over time) for EPC-QTD contracts.
 * Adds EAC forecast projection line when CPI is available.
 * Gated by epc_qtd_enabled feature flag.
 */
export function useEvmCurvaS(
  contratoId: string,
  tipoContratoOperacional?: string,
  tipoMedicao?: string,
) {
  const { enabled: flagEnabled } = useEpcQtdFlag();
  const isEpc = isContractEpcQtd(flagEnabled, tipoContratoOperacional, tipoMedicao);

  const { data, isLoading } = useQuery({
    queryKey: ['evm-curva-s', contratoId],
    queryFn: async (): Promise<CurvaSData | null> => {
      try {
        // Fetch time-series from emp_vw_evm_contrato (already has pv, ev, ac accumulated)
        const { data: rows, error } = await (supabase as any)
          .from('emp_vw_evm_contrato')
          .select('data, pv, ev, ac, bac, cpi, eac')
          .eq('contrato_id', contratoId)
          .order('data', { ascending: true });

        if (error || !rows || rows.length === 0) {
          console.debug('[EVM:CURVA] EVM_DATA_EMPTY for', contratoId);
          return null;
        }

        console.debug('[EVM:CURVA] EVM_SOURCE_SELECTED, rows:', rows.length);

        const lastRow = rows[rows.length - 1];
        const bac: number = lastRow.bac || 0;
        const cpi: number | null = lastRow.cpi;
        const eac: number | null = lastRow.eac;

        // Build points with optional forecast projection
        const points: CurvaSPoint[] = rows.map((r: any) => ({
          data: r.data,
          pv: r.pv || 0,
          ev: r.ev || 0,
          ac: r.ac || 0,
        }));

        // Add forecast projection: extend AC trend to EAC
        if (cpi != null && cpi > 0 && eac != null && bac > 0 && points.length >= 2) {
          const lastAc = lastRow.ac || 0;
          const lastEv = lastRow.ev || 0;

          // Only project if there's remaining work
          if (lastEv < bac && lastAc > 0) {
            // Mark last actual point as start of projection
            points[points.length - 1].eac_proj = lastAc;

            // Add projected completion point
            const lastDate = new Date(lastRow.data);
            // Estimate remaining duration: proportional to elapsed time × (remaining / done)
            const remainingRatio = (bac - lastEv) / lastEv;
            const projDays = Math.max(30, Math.round(remainingRatio * points.length));
            const projDate = new Date(lastDate);
            projDate.setDate(projDate.getDate() + projDays);

            points.push({
              data: projDate.toISOString().slice(0, 10),
              pv: bac, // PV converges to BAC
              ev: bac, // EV projected to BAC
              ac: eac, // AC projected to EAC
              eac_proj: eac,
            });
          }
        }

        return { points, bac, eac, cpi };
      } catch (err) {
        console.warn('[EVM:CURVA] Query exception:', err);
        return null;
      }
    },
    enabled: !!contratoId && isEpc,
    staleTime: 3 * 60 * 1000,
  });

  return {
    curvaSData: isEpc ? (data ?? null) : null,
    isLoading,
    isEpcQtd: isEpc,
  };
}
